import type { AccessGroupType } from "@/react/types/AccessGroup";

import type { ConnectionResponse } from "../api/admin/connections";
import type { PaginationHelper } from "./InternalAPIHelper";
import type { MemberTag } from "./MemberTag";
import type { NewsletterList } from "./Newsletter";

export const AUDIENCE_TYPE = {
  COMMUNITY_MEMBER: "CommunityMember",
  COMMUNITY_LEAD: "CommunityLead",
} as const;

export type AudienceType = (typeof AUDIENCE_TYPE)[keyof typeof AUDIENCE_TYPE];
export interface AudienceRoles {
  admin: boolean;
  moderator: boolean;
}

interface Contact {
  access_groups?: Pick<AccessGroupType, "id" | "name">[];
  activity_score?: number | null;
  avatar_url?: string;
  bio?: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
  email: string;
  headline?: string;
  id: number;
  invitation_status?: string | null;
  invited_at?: string | null;
  last_seen_at?: string | null;
  member_since?: string | null;
  member_since_overridden?: boolean;
  name: string;
  newsletter_lists?: Pick<NewsletterList, "id" | "name" | "newsletter_id">[];
  profile_confirmed_at?: string | null;
  public_uid: string;
  sqid: string;
  subscribed_to_marketing_emails?: boolean;
  subscribed_to_newsletters?: boolean;
  type: AudienceType;
  user_id: number;
}

export interface ContactListItem extends Contact {
  roles: AudienceRoles;
}

interface CommunityLead extends ContactListItem {
  type: typeof AUDIENCE_TYPE.COMMUNITY_LEAD;
}

interface CommunityMember extends ContactListItem {
  bio?: string;
  connection_to_current_community_member?: ConnectionResponse;
  location?: string;
  member_tags?: MemberTag[];
  mutual_connections_count?: number;
  type: typeof AUDIENCE_TYPE.COMMUNITY_MEMBER;
  visible_in_member_directory?: boolean;
}

export const isCommunityMember = (person: Person): person is CommunityMember =>
  person.type === AUDIENCE_TYPE.COMMUNITY_MEMBER;

export type Person = CommunityMember | CommunityLead;

/**
 * The minimal identity the shared contact modals (newsletter subscriptions and
 * deactivate) need, so any caller can build one without a full `Person`.
 */
export type AudienceContactIdentity = Pick<
  Person,
  "id" | "sqid" | "name" | "email" | "avatar_url" | "public_uid" | "type"
> & {
  has_active_paywall_subscriptions?: boolean;
};

export interface AudienceListResponse extends PaginationHelper<Person> {
  meta: {
    total_count: number;
  };
  next_search_after: string[];
}
